import { Box, BoxProps, Portal } from '@auviknetworks/iceberg-ui';
import React, { useEffect } from 'react';

type WithFullScreenProps = {
    /** Whether the full screen is active. */
    isFullScreen: boolean;
    /** The children to wrap. */
    children: React.ReactNode;
    /** The function to set the full screen state. */
    setIsFullScreen: (isFullScreen: boolean) => void;

    /** Backdrop props. */
    BackdropProps?: BoxProps;
};

/**
 * Wraps the children in a full screen component. Fullscreen mode will render over the
 * current page and close when the escape key is pressed.
 */
export const WithFullScreen = ({
    isFullScreen,
    children,
    setIsFullScreen,
    BackdropProps
}: WithFullScreenProps) => {
    // Close the full screen when the escape key is pressed
    useEffect(() => {
        const handleKeyUp = (event: KeyboardEvent) => {
            if (
                (event.key === 'Escape' || event.key === 'Esc') &&
                isFullScreen
            ) {
                setIsFullScreen(false);
            }
        };
        document.addEventListener('keyup', handleKeyUp);

        return () => {
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [isFullScreen, setIsFullScreen]);

    // If the full screen is not active, return the children
    if (!isFullScreen) {
        return children;
    }

    // If the full screen is active, return the full screen component
    return (
        <Portal>
            <Box
                data-testid="full-screen-backdrop"
                backgroundColor="rgba(0, 0, 0, 0.5)"
                height="100vh"
                left={0}
                padding="24px"
                position="fixed"
                top={0}
                width="100vw"
                zIndex={1300}
                {...BackdropProps}
            >
                {children}
            </Box>
        </Portal>
    );
};
